/**
 * VideoRegistry — Centralized video tracking, frame capture, and effect state.
 *
 * RULE: Only ONE drawImage per video per frame. RVFC callbacks only set dirty flags.
 * The RenderCoordinator calls `captureFrames()` once per frame in Phase 3.
 *
 * Phase B: Owns frame canvases, RVFC dirty flags, per-video effect chains,
 * and priority scores. Provides the single shared LUT renderer for all videos.
 */

import { WebGlLutRenderer } from "../canvas/utils/webglLut";
import { FrameCacheMemoryGovernor } from "./FrameCacheMemoryGovernor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type VideoTier = "active" | "warm" | "parked";

export interface VideoEffectChain {
  /** CSS filter string (brightness, contrast, blur, etc.) */
  cssFilter: string;
  /** LUT enabled + settings */
  lut: { enabled: boolean; lut: any; intensity: number; size: number } | null;
  /** Face blur enabled + cached rects */
  faceBlur: { enabled: boolean; rects: Array<{ x: number; y: number; width: number; height: number }> } | null;
  /** Gradient fade enabled + settings */
  gradientFade: { enabled: boolean; settings: any } | null;
}

export interface VideoEntry {
  elementId: string;
  video: HTMLVideoElement;
  tier: VideoTier;
  priority: number;
  /** ONE canvas per video for frame capture + effects. Created on demand. */
  frameCanvas: HTMLCanvasElement | null;
  frameCtx: CanvasRenderingContext2D | null;
  /** True when RVFC fires (new frame decoded). Reset after capture. */
  frameDirty: boolean;
  /** video.currentTime of last captured frame */
  lastCapturedTime: number;
  /** RVFC handle for cancellation */
  rvfcHandle: number | null;
  /** setInterval id used when requestVideoFrameCallback is unavailable (Firefox fallback). */
  rvfcFallbackIntervalId: number | null;
  /** Screen area in pixels² for priority sorting */
  screenArea: number;
  /** Whether the Konva node is visible at current playhead */
  visible: boolean;
  /** Per-video effect chain config */
  effects: VideoEffectChain;
  /** True after a SecurityError; stops further capture attempts to avoid error spam. */
  securityBlocked?: boolean;
  /**
   * Phase D scrub-frame cache. Keyed by quantized media time
   * (`Math.round(mediaTimeSec * 100)` — 10 ms precision). Bounded by
   * _governor; `frameCacheBytes` tracks how many bytes this entry has
   * currently reserved. Governor enforces the total-across-all-clips
   * budget, evicting oldest-first when a new reservation would overflow.
   */
  frameCache: Map<number, ImageBitmap>;
  frameCacheBytes: number;
  /**
   * Phase D rate-limit marker for speculative cache attempts. Rate-limits
   * createImageBitmap calls (which trigger a GPU->CPU readback) to the
   * FRAME_CACHE_SAMPLE_INTERVAL_MS cadence regardless of the video's
   * decode rate, so a 60fps video doesn't allocate 60 bitmaps per second.
   */
  lastCacheAttemptMs: number;
}

export interface FrameBudget {
  /** Max drawImage() calls this frame */
  maxCaptures: number;
  /** Max effect pipeline passes this frame */
  maxEffectPasses: number;
  /** Quality tier for this frame */
  qualityTier: "full" | "balanced" | "draft";
}

// ---------------------------------------------------------------------------
// Phase D frame cache governor + helpers
// ---------------------------------------------------------------------------

/**
 * Module-level governor shared by every VideoEntry's scrub-frame cache.
 * Exposed via getFrameCacheGovernor() so the D5 HUD and D6 monitor can
 * read stats and call reevaluate() without importing the class directly.
 */
const _governor = new FrameCacheMemoryGovernor();

/**
 * Time quantization for cached frame keys: 10 ms precision.
 * `Math.round(mediaTimeSec * 100)` maps 1.234s → 123.
 */
const TIME_QUANTIZATION_MS = 10;
const TIME_QUANTIZATION_FACTOR = 1000 / TIME_QUANTIZATION_MS; // 100

/**
 * Bytes per ImageBitmap assuming RGBA8. Used for governor reservation.
 * Matches the actual decoded size on every browser I could verify.
 */
function _bytesPerImageBitmap(width: number, height: number): number {
  return Math.max(1, width * height * 4);
}

export function getFrameCacheGovernor(): FrameCacheMemoryGovernor {
  return _governor;
}

/**
 * Aggregate frame-cache stats for the Perf HUD (Task D5). Returns the
 * governor's budget + usage + reserved-element count, plus the total
 * number of ImageBitmaps currently held across all entries.
 */
export function getGlobalFrameCacheStats(): {
  totalBudgetBytes: number;
  usedBytes: number;
  reservedElements: number;
  framesCached: number;
} {
  let framesCached = 0;
  for (const entry of _entries.values()) {
    framesCached += entry.frameCache.size;
  }
  return {
    ..._governor.getStats(),
    framesCached,
  };
}

// ---------------------------------------------------------------------------
// Phase D frame cache monitor (Task D6)
// ---------------------------------------------------------------------------

/**
 * Polling interval for the governor reevaluate + eviction pass. 2s is
 * long enough to amortize the cost but short enough to respond to
 * sudden heap pressure (e.g. the user opening a second project or a
 * JS leak elsewhere in the editor).
 */
const FRAME_CACHE_MONITOR_INTERVAL_MS = 2000;

let _frameCacheMonitorId: number | null = null;

/**
 * Start periodic governor re-evaluation. Called from Editor2TimelineShell
 * on mount. Idempotent — calling twice is a no-op.
 *
 * Each tick:
 * 1. Runs `_governor.reevaluate()` which adjusts the total budget based
 *    on current `performance.memory` pressure.
 * 2. Walks every VideoEntry and enforces the new per-clip budget by
 *    evicting oldest frames until each entry fits.
 *
 * The per-clip allocation is `totalBudget / max(1, entries.size)`.
 * If the budget shrinks (heap under pressure), excess frames are
 * evicted oldest-first until the entry fits its share. Bitmaps are
 * closed on eviction so native resources are released immediately.
 */
export function startFrameCacheMonitor(): void {
  if (_frameCacheMonitorId !== null) return;
  if (typeof window === "undefined") return;

  _frameCacheMonitorId = window.setInterval(() => {
    try {
      _governor.reevaluate();
      const entryCount = Math.max(1, _entries.size);
      const perClipBudget = _governor.getTotalBudgetBytes() / entryCount;
      for (const entry of _entries.values()) {
        while (entry.frameCacheBytes > perClipBudget && entry.frameCache.size > 0) {
          const oldestKey = entry.frameCache.keys().next().value;
          if (oldestKey === undefined) break;
          const oldBitmap = entry.frameCache.get(oldestKey);
          if (oldBitmap) {
            try { oldBitmap.close?.(); } catch { /* noop */ }
          }
          entry.frameCache.delete(oldestKey);
          const w = entry.video.videoWidth || 1920;
          const h = entry.video.videoHeight || 1080;
          const bytesPerFrame = _bytesPerImageBitmap(w, h);
          entry.frameCacheBytes -= bytesPerFrame;
        }
        // Reconcile the governor reservation with the post-eviction
        // byte count so released memory becomes available to other
        // clips on the next cache attempt.
        _governor.tryReserve(entry.elementId, Math.max(0, entry.frameCacheBytes));
      }
    } catch {
      /* monitor is best-effort — never throw into the interval */
    }
  }, FRAME_CACHE_MONITOR_INTERVAL_MS) as unknown as number;
}

/**
 * Stop the periodic monitor. Called from Editor2TimelineShell on unmount.
 * Idempotent.
 */
export function stopFrameCacheMonitor(): void {
  if (_frameCacheMonitorId === null) return;
  if (typeof window === "undefined") return;
  window.clearInterval(_frameCacheMonitorId);
  _frameCacheMonitorId = null;
}

// ---------------------------------------------------------------------------
// Singleton shared LUT renderer (replaces per-FilteredImage instances)
// ---------------------------------------------------------------------------

let _sharedLutRenderer: WebGlLutRenderer | null = null;

export function getSharedLutRenderer(): WebGlLutRenderer {
  if (!_sharedLutRenderer) {
    _sharedLutRenderer = new WebGlLutRenderer();
  }
  return _sharedLutRenderer;
}

export function disposeSharedLutRenderer(): void {
  _sharedLutRenderer?.dispose();
  _sharedLutRenderer = null;
}

// ---------------------------------------------------------------------------
// VideoRegistry
// ---------------------------------------------------------------------------

const _entries = new Map<string, VideoEntry>();

function _defaultEffects(): VideoEffectChain {
  return { cssFilter: "none", lut: null, faceBlur: null, gradientFade: null };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register a video element. Creates the entry if new, updates tier if existing.
 */
export function registerVideo(
  elementId: string,
  video: HTMLVideoElement,
  tier: VideoTier = "active",
): VideoEntry {
  let entry = _entries.get(elementId);
  if (entry) {
    entry.video = video;
    entry.tier = tier;
    return entry;
  }

  entry = {
    elementId,
    video,
    tier,
    priority: 99,
    frameCanvas: null,
    frameCtx: null,
    frameDirty: true,
    lastCapturedTime: -1,
    rvfcHandle: null,
    rvfcFallbackIntervalId: null,
    screenArea: 0,
    visible: false,
    effects: _defaultEffects(),
    frameCache: new Map<number, ImageBitmap>(),
    frameCacheBytes: 0,
    lastCacheAttemptMs: 0,
  };

  _entries.set(elementId, entry);

  // Register RVFC if supported — only sets dirty flag, never draws
  _registerRvfc(entry);

  return entry;
}

/**
 * Unregister a video, clean up frame canvas, RVFC, and the scrub-frame cache.
 */
export function unregisterVideo(elementId: string): void {
  const entry = _entries.get(elementId);
  if (!entry) return;
  _cancelRvfc(entry);
  entry.frameCanvas = null;
  entry.frameCtx = null;
  // Phase D — close every cached ImageBitmap (releases native resources
  // on Chromium) and release the governor reservation so other clips
  // can reclaim the budget.
  for (const bitmap of entry.frameCache.values()) {
    try { bitmap.close?.(); } catch { /* noop */ }
  }
  entry.frameCache.clear();
  entry.frameCacheBytes = 0;
  _governor.release(elementId);
  _entries.delete(elementId);
}

/**
 * Update visibility and priority for a video.
 */
export function updateVideoState(
  elementId: string,
  opts: { visible?: boolean; screenArea?: number; priority?: number; tier?: VideoTier },
): void {
  const entry = _entries.get(elementId);
  if (!entry) return;
  if (opts.visible !== undefined) entry.visible = opts.visible;
  if (opts.screenArea !== undefined) entry.screenArea = opts.screenArea;
  if (opts.priority !== undefined) entry.priority = opts.priority;
  if (opts.tier !== undefined) entry.tier = opts.tier;
}

/**
 * Update the effect chain for a video.
 */
export function updateVideoEffects(elementId: string, effects: Partial<VideoEffectChain>): void {
  const entry = _entries.get(elementId);
  if (!entry) return;
  Object.assign(entry.effects, effects);
}

// ---------------------------------------------------------------------------
// Phase D scrub-frame cache API
// ---------------------------------------------------------------------------

/**
 * Cache an ImageBitmap for `elementId` at the given mediaTimeSec. The
 * bitmap is stored in the entry's ring buffer keyed by 10 ms-quantized
 * time. Memory is reserved via the shared governor; if the reservation
 * fails the oldest frame is evicted and the reservation is retried once.
 * On persistent failure the caller's bitmap is NOT stored and is NOT
 * closed — the caller retains ownership.
 *
 * No-op if the element isn't registered.
 */
export function cacheFrameAt(
  elementId: string,
  mediaTimeSec: number,
  bitmap: ImageBitmap
): void {
  const entry = _entries.get(elementId);
  if (!entry) return;

  const width = entry.video.videoWidth || bitmap.width || 1920;
  const height = entry.video.videoHeight || bitmap.height || 1080;
  const bytesPerFrame = _bytesPerImageBitmap(width, height);
  const quantizedKey = Math.round(mediaTimeSec * TIME_QUANTIZATION_FACTOR);

  // If this key already exists, close the old bitmap before replacing it
  // (otherwise we'd leak native resources on Chromium).
  const existing = entry.frameCache.get(quantizedKey);
  if (existing) {
    try { existing.close?.(); } catch { /* noop */ }
    entry.frameCache.delete(quantizedKey);
    entry.frameCacheBytes -= bytesPerFrame;
  }

  // Try to reserve memory for the new frame.
  const nextBytes = entry.frameCacheBytes + bytesPerFrame;
  if (!_governor.tryReserve(elementId, nextBytes)) {
    // Over-budget. Evict the oldest frame (Map iteration order is insertion
    // order, so .keys().next().value is the oldest).
    const oldestKey = entry.frameCache.keys().next().value;
    if (oldestKey === undefined) {
      // Empty cache can't satisfy a reservation at all → drop the new frame.
      return;
    }
    const oldBitmap = entry.frameCache.get(oldestKey);
    if (oldBitmap) {
      try { oldBitmap.close?.(); } catch { /* noop */ }
    }
    entry.frameCache.delete(oldestKey);
    entry.frameCacheBytes -= bytesPerFrame;
    // Retry the reservation.
    const retryBytes = entry.frameCacheBytes + bytesPerFrame;
    if (!_governor.tryReserve(elementId, retryBytes)) {
      // Still over-budget — caller's bitmap is untouched.
      return;
    }
  }

  entry.frameCache.set(quantizedKey, bitmap);
  entry.frameCacheBytes += bytesPerFrame;
}

/**
 * Look up the nearest cached frame for `elementId` within `toleranceSec`
 * of `mediaTimeSec`. Returns null if no frame is close enough.
 *
 * This is the read side of the scrub-cache contract: during paused/scrub
 * mode, the canvas renderer calls this to find a frame close to the
 * target time without issuing a seek. Range search is O(n) where n is
 * the frame count per clip (bounded by the governor's per-clip capacity,
 * so ≤ ~30 in practice).
 */
export function getCachedFrameNear(
  elementId: string,
  mediaTimeSec: number,
  toleranceSec: number
): ImageBitmap | null {
  const entry = _entries.get(elementId);
  if (!entry || entry.frameCache.size === 0) return null;

  const targetKey = Math.round(mediaTimeSec * TIME_QUANTIZATION_FACTOR);
  const toleranceKeys = Math.ceil(toleranceSec * TIME_QUANTIZATION_FACTOR);

  let bestKey: number | null = null;
  let bestDelta = Infinity;
  for (const key of entry.frameCache.keys()) {
    const delta = Math.abs(key - targetKey);
    if (delta <= toleranceKeys && delta < bestDelta) {
      bestKey = key;
      bestDelta = delta;
      if (delta === 0) break; // exact hit, no point scanning further
    }
  }
  return bestKey !== null ? entry.frameCache.get(bestKey) ?? null : null;
}

/**
 * Get a read-only snapshot of all entries (for diagnostic/telemetry).
 */
export function getVideoEntries(): ReadonlyMap<string, VideoEntry> {
  return _entries;
}

/**
 * Get a specific entry.
 */
export function getVideoEntry(elementId: string): VideoEntry | undefined {
  return _entries.get(elementId);
}

/**
 * Returns entries sorted by priority (lowest number = highest priority).
 */
export function getVideosByPriority(): VideoEntry[] {
  return Array.from(_entries.values())
    .filter((e) => e.tier === "active" && e.visible)
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Phase 3: Capture frames for all active videos within the frame budget.
 * Called ONCE per frame by the RenderCoordinator.
 *
 * Returns the number of frames actually captured this tick.
 */
export function captureFrames(budget: FrameBudget): number {
  const sorted = getVideosByPriority();
  let captured = 0;

  for (const entry of sorted) {
    if (captured >= budget.maxCaptures) break;

    // Skip if no new frame available
    if (!entry.frameDirty) continue;

    // Skip permanently-blocked entries (cross-origin source without CORS headers).
    // Once we've seen a SecurityError, retrying every frame just spams the error
    // path; the HUD already surfaced the issue via vb:video-cors-blocked.
    if (entry.securityBlocked) continue;

    // Skip if video is not ready
    const v = entry.video;
    if (v.readyState < 2) continue; // HAVE_CURRENT_DATA

    // Ensure frame canvas exists
    _ensureFrameCanvas(entry, budget.qualityTier);
    const { frameCanvas, frameCtx } = entry;
    if (!frameCanvas || !frameCtx) continue;

    // Apply CSS filter if any
    frameCtx.filter = entry.effects.cssFilter !== "none" ? entry.effects.cssFilter : "none";

    // ONE drawImage per video. Period.
    try {
      frameCtx.drawImage(v, 0, 0, frameCanvas.width, frameCanvas.height);
    } catch (err) {
      if (err instanceof DOMException && err.name === "SecurityError") {
        // Cross-origin source without CORS headers. Mark entry permanently blocked
        // so we don't spam errors every frame, and surface a single diagnostic event
        // so the admin Perf HUD can show which element is blocked.
        if (!entry.securityBlocked) {
          entry.securityBlocked = true;
          entry.frameDirty = false;
          try {
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent("vb:video-cors-blocked", {
                  detail: { elementId: entry.elementId },
                })
              );
            }
          } catch { /* noop */ }
        }
      }
      continue;
    }

    entry.frameDirty = false;
    entry.lastCapturedTime = v.currentTime;
    captured++;
  }

  return captured;
}

/**
 * Phase 4: Apply effects to all captured frame canvases within budget.
 * Called ONCE per frame by the RenderCoordinator after captureFrames.
 *
 * Returns the number of effect passes completed.
 */
export function applyEffects(budget: FrameBudget): number {
  const sorted = getVideosByPriority();
  let passes = 0;

  for (const entry of sorted) {
    if (passes >= budget.maxEffectPasses) break;
    if (!entry.frameCanvas || !entry.frameCtx) continue;

    const { effects, frameCanvas, frameCtx } = entry;
    let didWork = false;

    // LUT color grading (shared singleton renderer)
    if (effects.lut?.enabled && effects.lut.lut) {
      const renderer = getSharedLutRenderer();
      const result = renderer.render(
        frameCanvas,
        effects.lut.intensity,
        effects.lut.size,
      );
      if (result) {
        frameCtx.filter = "none";
        frameCtx.drawImage(result, 0, 0, frameCanvas.width, frameCanvas.height);
        didWork = true;
      }
    }

    // Face blur (draws blur regions directly on the frame canvas)
    if (effects.faceBlur?.enabled && effects.faceBlur.rects.length > 0) {
      for (const rect of effects.faceBlur.rects) {
        frameCtx.save();
        frameCtx.filter = "blur(12px)";
        frameCtx.drawImage(
          frameCanvas,
          rect.x, rect.y, rect.width, rect.height,
          rect.x, rect.y, rect.width, rect.height,
        );
        frameCtx.restore();
      }
      didWork = true;
    }

    if (didWork) passes++;
  }

  return passes;
}

/**
 * Clear all entries. Called on editor unmount.
 */
export function clearRegistry(): void {
  for (const entry of _entries.values()) {
    _cancelRvfc(entry);
  }
  _entries.clear();
  disposeSharedLutRenderer();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const QUALITY_SCALE: Record<string, number> = {
  full: 1,
  balanced: 0.75,
  draft: 0.5,
};

const RVFC_FALLBACK_INTERVAL_MS = 30;

/**
 * Rate limit for speculative frame caching. 250 ms = 4 Hz — enough scrub
 * coverage (user can drag by ~250 ms and still find a nearby cached
 * frame) without the GPU→CPU readback cost of cloning every decoded
 * frame at 60 fps.
 */
const FRAME_CACHE_SAMPLE_INTERVAL_MS = 250;

/**
 * Speculatively cache the current decoded frame of `entry.video` as an
 * ImageBitmap. Fire-and-forget: errors are silently ignored (CORS,
 * decoder busy, tab hidden). Rate-limited to FRAME_CACHE_SAMPLE_INTERVAL_MS
 * per entry so high-framerate videos don't flood the governor.
 *
 * Called from both the native rVFC onFrame callback and the Firefox
 * setInterval fallback.
 */
async function _speculativelyCacheCurrentFrame(entry: VideoEntry): Promise<void> {
  if (typeof createImageBitmap !== "function") return;
  if (entry.securityBlocked) return;

  const video = entry.video;
  if (!video || video.readyState < 2) return;

  const nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
  if (nowMs - entry.lastCacheAttemptMs < FRAME_CACHE_SAMPLE_INTERVAL_MS) return;
  entry.lastCacheAttemptMs = nowMs;

  const mediaTime = video.currentTime;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(video);
  } catch {
    // Non-fatal: CORS, decoder busy, video not ready, tab hidden, etc.
    // The next rVFC tick will retry after the sample interval elapses.
    return;
  }

  // After the await, verify this entry is STILL the one registered under
  // entry.elementId. A race (unregister → re-register with the same id
  // for a different video) could otherwise write the bitmap onto a stale
  // entry. If the identity check fails, close the bitmap and bail.
  const current = _entries.get(entry.elementId);
  if (current !== entry) {
    try { bitmap.close?.(); } catch { /* noop */ }
    return;
  }

  cacheFrameAt(entry.elementId, mediaTime, bitmap);
}

function _ensureFrameCanvas(entry: VideoEntry, quality: string): void {
  const scale = QUALITY_SCALE[quality] ?? 1;
  const w = Math.round((entry.video.videoWidth || 1920) * scale);
  const h = Math.round((entry.video.videoHeight || 1080) * scale);

  if (entry.frameCanvas && entry.frameCanvas.width === w && entry.frameCanvas.height === h) {
    return; // already correct size
  }

  if (!entry.frameCanvas) {
    entry.frameCanvas = document.createElement("canvas");
  }
  entry.frameCanvas.width = w;
  entry.frameCanvas.height = h;
  entry.frameCtx = entry.frameCanvas.getContext("2d");
}

function _registerRvfc(entry: VideoEntry): void {
  const v = entry.video as any;
  if (typeof v.requestVideoFrameCallback === "function") {
    const onFrame = () => {
      entry.frameDirty = true;
      if (_entries.has(entry.elementId)) {
        entry.rvfcHandle = v.requestVideoFrameCallback(onFrame);
      }
      // Phase D — speculatively cache the decoded frame. Fire and forget;
      // the helper rate-limits itself and swallows all errors.
      void _speculativelyCacheCurrentFrame(entry);
    };
    entry.rvfcHandle = v.requestVideoFrameCallback(onFrame);
    return;
  }
  // Fallback for browsers without requestVideoFrameCallback (Firefox).
  // Flip frameDirty on an interval while the video is actually playing.
  // Idle/paused videos stay clean so we don't waste capture budget.
  entry.rvfcFallbackIntervalId = window.setInterval(() => {
    if (!_entries.has(entry.elementId)) return;
    const video = entry.video;
    if (!video) return;
    if (video.paused || video.readyState < 2) return;
    entry.frameDirty = true;
    // Phase D — speculatively cache on the fallback tick as well. Same
    // rate-limit applies regardless of which path fires.
    void _speculativelyCacheCurrentFrame(entry);
  }, RVFC_FALLBACK_INTERVAL_MS) as unknown as number;
}

function _cancelRvfc(entry: VideoEntry): void {
  if (entry.rvfcHandle !== null) {
    const v = entry.video as any;
    if (typeof v.cancelVideoFrameCallback === "function") {
      v.cancelVideoFrameCallback(entry.rvfcHandle);
    }
    entry.rvfcHandle = null;
  }
  if (entry.rvfcFallbackIntervalId !== null) {
    window.clearInterval(entry.rvfcFallbackIntervalId);
    entry.rvfcFallbackIntervalId = null;
  }
}
